import { ReactNode, useMemo } from 'react';
import { Badge, Box, Button, DataTable, DataTableBody, Field, Flex, Text } from '@optiaxiom/react';
import { IconCopy } from '@optimizely/axiom-icons';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { useOptimizelyUser } from '@frontend/hooks';
import { copyToClipboard } from '@frontend/utils/copyToClipboard';

import { formatExecutionTime } from '../../../utils/dateUtils';

import { qualityScoreField } from './ExecutionDetailsCard.css';
import { ExecutionStatusBadge } from './ExecutionStatusBadge';

type ExecutionDetailsCardProps = {
  executionId: string;
  status: string;
  agentName?: string;
  createdAt?: string;
  executionTime?: number;
  tokenUsage?: {
    total_tokens: number;
    input_tokens: number;
    output_tokens: number;
  };
  evaluationScore?: number | null;
  baselineEvaluationScore?: number | null;
  variant?: 'compact' | 'detailed';
  hideStatusBadge?: boolean;
};

type ExecutionDetailRow = {
  field: string | ReactNode;
  value: string | ReactNode;
};

const columnHelper = createColumnHelper<ExecutionDetailRow>();

export function ExecutionDetailsCard({
  executionId,
  status,
  agentName,
  createdAt,
  executionTime,
  tokenUsage,
  evaluationScore,
  baselineEvaluationScore,
  variant = 'compact',
  hideStatusBadge = false,
}: ExecutionDetailsCardProps) {
  const formatDuration = (time: number): string => {
    return `${time.toFixed(2)}s`;
  };

  const { isOptimizelyUser } = useOptimizelyUser();

  const shouldShowTokenUsage = tokenUsage && isOptimizelyUser;

  const columns = useMemo(
    () => [
      columnHelper.accessor('field', {
        header: 'Field',
        size: 120,
        cell: info => <Text fontWeight="500">{info.getValue()}</Text>,
      }),
      columnHelper.accessor('value', {
        header: 'Value',
        cell: info => info.getValue(),
      }),
    ],
    []
  );

  const executionDetailsData = useMemo(() => {
    const rows: ExecutionDetailRow[] = [];

    if (agentName) {
      rows.push({
        field: 'Agent Name',
        value: (
          <Flex flexDirection="row" gap="8" alignItems="center" w="full">
            <Text truncate flex="1">
              {agentName}
            </Text>
            <Button
              appearance="subtle"
              size="sm"
              icon={<IconCopy />}
              onClick={() => copyToClipboard(agentName)}
              aria-label="Copy agent name"
            />
          </Flex>
        ),
      });
    }

    rows.push({
      field: 'Execution ID',
      value: (
        <Flex flexDirection="row" gap="8" alignItems="center" w="full">
          <Text truncate flex="1">
            {executionId}
          </Text>
          <Button
            appearance="subtle"
            size="sm"
            icon={<IconCopy />}
            onClick={() => copyToClipboard(executionId)}
            aria-label="Copy execution ID"
          />
        </Flex>
      ),
    });

    if (createdAt) {
      rows.push({
        field: 'Execution Time',
        value: formatExecutionTime(createdAt),
      });
    }

    if (executionTime) {
      rows.push({
        field: 'Duration',
        value: formatDuration(executionTime),
      });
    }

    if (evaluationScore !== null && evaluationScore !== undefined) {
      const threshold = baselineEvaluationScore ?? 70;
      rows.push({
        field: (
          <Field
            className={qualityScoreField}
            label="Quality Score"
            info="Opal calculates a quality score by evaluating its response against provided output examples for correctness, relevance, and tone."
          />
        ),
        value: (
          <Flex flexDirection="row" alignItems="center" gap="8">
            <Text>{evaluationScore.toFixed(0)}%</Text>
            <Badge intent={evaluationScore >= threshold ? 'success' : 'danger'}>
              {evaluationScore >= threshold ? 'Passed' : 'Failed'}
            </Badge>
          </Flex>
        ),
      });
    }

    if (shouldShowTokenUsage) {
      rows.push({
        field: 'Tokens',
        value: `${tokenUsage.total_tokens} total (${tokenUsage.input_tokens} input, ${tokenUsage.output_tokens} output)`,
      });
    }
    return rows;
  }, [
    executionId,
    agentName,
    createdAt,
    executionTime,
    shouldShowTokenUsage,
    tokenUsage,
    evaluationScore,
    baselineEvaluationScore,
  ]);

  const table = useReactTable({
    data: executionDetailsData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: { pagination: { pageIndex: 0, pageSize: 10 } },
  });

  if (variant === 'compact') {
    return (
      <Box border="1" borderColor="border.default" rounded="md">
        <Flex flexDirection="column">
          <Box bg="bg.secondary" p="12">
            <Text fontSize="sm" fontFamily="mono">
              <strong>Execution ID:</strong> {executionId}
            </Text>
            <Text fontSize="sm" fontFamily="mono">
              <strong>Status:</strong> {status}
            </Text>
            {shouldShowTokenUsage && (
              <Text fontSize="sm" fontFamily="mono">
                <strong>Tokens:</strong> {tokenUsage.total_tokens} total ({tokenUsage.input_tokens}{' '}
                input, {tokenUsage.output_tokens} output)
              </Text>
            )}
          </Box>
        </Flex>
      </Box>
    );
  }

  return (
    <Box rounded="md">
      <Field label="Execution Details" />
      <Box bg="bg.default" p="12">
        <Flex flexDirection="column" gap="8">
          <DataTable table={table}>
            <DataTableBody />
          </DataTable>
          {variant === 'detailed' && !hideStatusBadge && (
            <Flex flexDirection="row" alignItems="center" gap="8">
              <Text fontSize="sm" fontWeight="600">
                Status:
              </Text>
              <ExecutionStatusBadge status={status} showAsRunning />
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
}
